import type { TimedMusicXmlNote } from "./songEngine";
import type { SongPlaybackTimeline } from "./songPlayback";
import type { PracticeNoteScore, PracticePitchFrame, PracticeScoringOptions } from "./noteScoring";
import { computeAdaptiveNoteWindows } from "./tempoAdaptiveTiming";

export type PracticeEventStatus = "pending" | "hit" | "partial" | "late" | "miss";

/** Evento de ataque simultáneo (acorde / varias voces en el mismo instante). */
export interface PracticePlaybackEvent {
  attackTime: number;
  startMs: number;
  endMs: number;
  durationMs: number;
  noteIndices: number[];
  expectedMidis: number[];
  earlyMs: number;
  lateMs: number;
  sustainTailMs: number;
  lateGraceMs: number;
  status: PracticeEventStatus;
}

export const POLYPHONIC_EVENT_MIN_NOTES = 2;

/** Cobertura mínima para considerar un evento polifónico como acierto (no exige 100%). */
export const DEFAULT_POLY_COVERAGE_HIT = 1;
export const DEFAULT_POLY_COVERAGE_PARTIAL = 0.5;

export function isPolyphonicEvent(event: PracticePlaybackEvent): boolean {
  return event.expectedMidis.length >= POLYPHONIC_EVENT_MIN_NOTES;
}

export function buildPracticePlaybackEvents(
  notes: TimedMusicXmlNote[],
  timeline: SongPlaybackTimeline
): PracticePlaybackEvent[] {
  const attackTimes = [
    ...new Set(
      notes.filter((n) => !n.isRest && n.midi > 0).map((n) => n.startTime)
    ),
  ].sort((a, b) => a - b);

  const events: PracticePlaybackEvent[] = [];

  for (const attackTime of attackTimes) {
    const atAttack = notes
      .map((n, noteIndex) => ({ n, noteIndex }))
      .filter(({ n }) => !n.isRest && n.midi > 0 && n.startTime === attackTime);

    if (atAttack.length === 0) continue;

    const segment = timeline.segments.find((s) => s.startTime === attackTime);
    if (!segment) continue;

    const durationMs = Math.max(60, segment.endMs - segment.startMs);
    events.push({
      attackTime,
      startMs: segment.startMs,
      endMs: segment.endMs,
      durationMs,
      noteIndices: atAttack.map(({ noteIndex }) => noteIndex),
      expectedMidis: atAttack.map(({ n }) => n.midi),
      earlyMs: 0,
      lateMs: 0,
      sustainTailMs: 0,
      lateGraceMs: 0,
      status: "pending",
    });
  }

  const sorted = [...events].sort((a, b) => a.startMs - b.startMs);
  for (let i = 0; i < sorted.length; i++) {
    const ev = sorted[i];
    const next = sorted[i + 1];
    const ioiMs = next ? next.startMs - ev.startMs : ev.durationMs;
    const windows = computeAdaptiveNoteWindows(ev.durationMs, ioiMs);
    ev.earlyMs = windows.earlyMs;
    ev.lateMs = windows.lateMs;
    ev.sustainTailMs = windows.sustainTailMs;
    ev.lateGraceMs = windows.lateGraceMs;
  }

  return events;
}

export function eventWindowEndMs(ev: PracticePlaybackEvent): number {
  return Math.max(ev.startMs + ev.lateMs, ev.endMs + ev.sustainTailMs);
}

export function eventHardEndMs(ev: PracticePlaybackEvent): number {
  return eventWindowEndMs(ev) + ev.lateGraceMs;
}

export function getEventForNoteIndex(
  events: PracticePlaybackEvent[],
  noteIndex: number
): PracticePlaybackEvent | undefined {
  return events.find((e) => e.noteIndices.includes(noteIndex));
}

export function eventCoverage(
  event: PracticePlaybackEvent,
  noteScores: PracticeNoteScore[]
): number {
  if (event.expectedMidis.length === 0) return 0;
  let matched = 0;
  for (const idx of event.noteIndices) {
    const s = noteScores.find((n) => n.noteIndex === idx);
    if (s && (s.status === "hit" || s.status === "late")) matched += 1;
  }
  return matched / event.noteIndices.length;
}

export function refreshEventStatuses(
  events: PracticePlaybackEvent[],
  noteScores: PracticeNoteScore[],
  partialThreshold = DEFAULT_POLY_COVERAGE_PARTIAL
): boolean {
  let changed = false;
  for (const ev of events) {
    if (ev.status === "miss") continue;
    const cov = eventCoverage(ev, noteScores);
    const allResolved = ev.noteIndices.every((idx) => {
      const s = noteScores.find((n) => n.noteIndex === idx);
      return s && s.status !== "pending";
    });

    let next: PracticeEventStatus = ev.status;
    if (cov >= DEFAULT_POLY_COVERAGE_HIT && allResolved) {
      const anyLate = ev.noteIndices.some((idx) => {
        const s = noteScores.find((n) => n.noteIndex === idx);
        return s?.status === "late";
      });
      next = anyLate ? "late" : "hit";
    } else if (allResolved && cov >= partialThreshold) {
      next = "partial";
    } else if (allResolved) {
      next = "miss";
    } else if (cov > 0) {
      next = "pending";
    }

    if (next !== ev.status) {
      ev.status = next;
      changed = true;
    }
  }
  return changed;
}

export function finalizeExpiredPracticeEvents(
  events: PracticePlaybackEvent[],
  noteScores: PracticeNoteScore[],
  playbackMs: number
): boolean {
  let changed = false;
  for (const ev of events) {
    if (playbackMs <= eventHardEndMs(ev)) continue;
    for (const idx of ev.noteIndices) {
      const s = noteScores.find((n) => n.noteIndex === idx);
      if (s && s.status === "pending") {
        s.status = "miss";
        changed = true;
      }
    }
  }
  if (refreshEventStatuses(events, noteScores)) changed = true;
  return changed;
}

/** Etiqueta de acorde esperado para UI (ej. C4+E4+G4). */
export function formatExpectedEventLabel(midis: number[], labelFn: (midi: number) => string): string {
  return midis.map(labelFn).join(" + ");
}

/** Primera nota del evento aún sin marcar que coincide con el pitch oído. */
export function findLearnNoteIndexForHeardPitch(
  noteIndices: number[],
  notes: TimedMusicXmlNote[],
  heardMidi: number,
  cents: number,
  alreadyHit: ReadonlySet<number>,
  pitchMatches: (heardMidi: number, expectedMidi: number, cents: number) => boolean
): number | null {
  for (const idx of noteIndices) {
    if (alreadyHit.has(idx)) continue;
    const n = notes[idx];
    if (!n || n.isRest) continue;
    if (pitchMatches(heardMidi, n.midi, cents)) return idx;
  }
  return null;
}

export function isLearnEventComplete(
  noteIndices: number[],
  hitIndices: ReadonlySet<number>
): boolean {
  return noteIndices.length > 0 && noteIndices.every((idx) => hitIndices.has(idx));
}
